import React from 'react';

// Wraps a dynamic import in a component that can be preloaded ahead of the
// first render. If the module is already loaded by the time React renders
// it, the component renders synchronously (no Suspense fallback flash) —
// this avoids prerendered content being briefly removed during hydration.
export const lazyWithPreload = (factory) => {
  let Component = null;
  let promise = null;

  const load = () => {
    if (!promise) {
      promise = factory().then((module) => {
        Component = module.default;
        return module;
      });
    }
    return promise;
  };

  const LazyComponent = (props) => {
    if (Component) {
      return <Component {...props} />;
    }
    throw load();
  };

  LazyComponent.preload = load;

  return LazyComponent;
};

export const HomePage = lazyWithPreload(() => import('./components/HomePage'));
export const BlogPage = lazyWithPreload(() => import('./components/BlogPage'));
export const PostPage = lazyWithPreload(() => import('./components/PostPage'));
export const PrivacyPolicy = lazyWithPreload(() => import('./components/PrivacyPolicy'));
export const CookiePolicy = lazyWithPreload(() => import('./components/CookiePolicy'));
export const TermsConditions = lazyWithPreload(() => import('./components/TermsConditions'));
export const AboutUs = lazyWithPreload(() => import('./components/AboutUs'));
export const FontsIndexPage = lazyWithPreload(() => import('./components/FontsIndexPage'));
export const ContactPage = lazyWithPreload(() => import('./components/ContactPage'));
export const UnicodeSymbolsPage = lazyWithPreload(() => import('./components/UnicodeSymbolsPage'));
export const BackwardsTextPage = lazyWithPreload(() => import('./components/BackwardsTextPage'));
export const KaomojiPage = lazyWithPreload(() => import('./components/KaomojiPage'));
export const LennyFacesPage = lazyWithPreload(() => import('./components/LennyFacesPage'));
export const ImageDotArtPage = lazyWithPreload(() => import('./components/ImageDotArtPage'));
export const UnicodeCompatibilityPage = lazyWithPreload(() => import('./components/UnicodeCompatibilityPage'));
export const AsciiArtPage = lazyWithPreload(() => import('./components/AsciiArtPage'));
export const UsernamePage = lazyWithPreload(() => import('./components/UsernamePage'));
export const NumberFontPage = lazyWithPreload(() => import('./components/NumberFontPage'));
export const TattooFontPage = lazyWithPreload(() => import('./components/TattooFontPage'));
export const CaseConverterPage = lazyWithPreload(() => import('./components/CaseConverterPage'));
export const WordCounterPage = lazyWithPreload(() => import('./components/WordCounterPage'));
export const TextStylerPage = lazyWithPreload(() => import('./components/TextStylerPage'));
export const FontImageGeneratorPage = lazyWithPreload(() => import('./components/FontImageGeneratorPage'));
export const TextRepeaterPage = lazyWithPreload(() => import('./components/TextRepeaterPage'));
export const WingdingsTranslatorPage = lazyWithPreload(() => import('./components/WingdingsTranslatorPage'));
export const MonogramMakerPage = lazyWithPreload(() => import('./components/MonogramMakerPage'));
export const FullscreenTextPage = lazyWithPreload(() => import('./components/FullscreenTextPage'));

// Mirrors the route table in App.js so the matching chunk for the current
// URL can be preloaded before the initial hydration.
export const matchRouteComponent = (pathname) => {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/') return HomePage;
  if (path === '/backwards-text' || path === '/fonts/backwards-text') return BackwardsTextPage;
  if (path === '/fonts') return FontsIndexPage;
  if (path.startsWith('/fonts/')) return HomePage;
  if (path === '/blog') return BlogPage;
  if (path.startsWith('/blog/')) return PostPage;
  if (path === '/privacy-policy') return PrivacyPolicy;
  if (path === '/cookie-policy') return CookiePolicy;
  if (path === '/terms-conditions') return TermsConditions;
  if (path === '/about' || path === '/AboutUs') return AboutUs;
  if (path === '/contact') return ContactPage;
  if (path === '/unicode-symbols') return UnicodeSymbolsPage;
  if (path === '/kaomoji') return KaomojiPage;
  if (path === '/lenny-faces') return LennyFacesPage;
  if (path === '/dot-art-generator') return ImageDotArtPage;
  if (path === '/ascii-art-generator') return AsciiArtPage;
  if (path === '/username-generator') return UsernamePage;
  if (path === '/number-font-generator') return NumberFontPage;
  if (path === '/tattoo-font-generator') return TattooFontPage;
  if (path === '/case-converter') return CaseConverterPage;
  if (path === '/word-counter') return WordCounterPage;
  if (path === '/text-styler') return TextStylerPage;
  if (path === '/font-image-generator') return FontImageGeneratorPage;
  if (path === '/text-repeater') return TextRepeaterPage;
  if (path === '/wingdings-translator') return WingdingsTranslatorPage;
  if (path === '/monogram-maker') return MonogramMakerPage;
  if (path === '/fullscreen-text') return FullscreenTextPage;
  if (path === '/unicode-font-compatibility') return UnicodeCompatibilityPage;
  return null;
};
